import type { DropdownOption, SelectProps, SelectVariant } from '@stenajs-webui/select';
import type { ReactElement } from 'react';
import type { GroupBase, Options, SelectComponentsConfig } from 'react-select';
import type { TrackerCategory } from '../../common/tracking/trackerService';
import { isDefined } from '../../common/utils/isDefined';
import { useDropdownField } from '../../use-cases/multiple-create/hooks/useDropdownField';
import type { TrackerAction } from '../../use-cases/simple-create/hooks/UseTrackingTextInput';
import { Dropdown, type GroupedOptionsType } from './Dropdown';

export interface CommonDropdownFieldProps<TValue>
  extends Omit<
    Partial<SelectProps<DropdownOption<NonNullable<TValue>>>>,
    'isDisabled' | 'onChange' | 'options' | 'components' | 'value' | 'inputId'
  > {
  changed?: boolean;
  components?: SelectComponentsConfig<
    DropdownOption<NonNullable<TValue>>,
    false,
    GroupBase<DropdownOption<NonNullable<TValue>>>
  >;
  disabled?: boolean;
  isValid?: boolean;
  onValueChange: (value: TValue) => void;
  options:
    | GroupedOptionsType<DropdownOption<NonNullable<TValue>>>
    | Options<DropdownOption<NonNullable<TValue>>>;
  trackerAction: TrackerAction;
  trackerCategory: TrackerCategory;
  transformOption: (v: NonNullable<TValue>) => DropdownOption<NonNullable<TValue>>;
  value: TValue | undefined;
  variant?: SelectVariant;
}

export function CommonDropdownField<TValue>({
  transformOption,
  disabled,
  options,
  variant,
  isValid = true,
  onValueChange,
  changed,
  value,
  trackerAction,
  trackerCategory,
  id,
  ...props
}: CommonDropdownFieldProps<TValue>): ReactElement<CommonDropdownFieldProps<TValue>> {
  const internalOnChange = (value: TValue | undefined) => {
    if (isDefined(value)) {
      onValueChange?.(value);
    }
  };

  const { onChange: dropdownOnChange, value: dropdownValue } = useDropdownField({
    onChange: internalOnChange,
    options,
    transformOption,
    value,
    trackerCategory,
    trackerAction,
  });

  return (
    <Dropdown
      inputId={id}
      onChange={dropdownOnChange}
      options={options}
      isDisabled={disabled}
      value={dropdownValue ?? null}
      isEdited={changed}
      variant={isValid ? variant : 'error'}
      {...props}
    />
  );
}
